import { useMemo, useState } from "react";

import { Box, Popover } from "@mui/material";
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import ClosingChip from "./ClosingChip";
import {
  C,
  MONO,
  dayKey,
  formatCompactDollar,
  loanId,
  sumLoanAmounts,
} from "./closingCalendarUtils";

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MAX_VISIBLE_PER_DAY = 3;

const DayCell = ({ date, inMonth, loans, onSelectLoan, isLastCol, isLastRow }) => {
  const [moreAnchor, setMoreAnchor] = useState(null);
  const today = isToday(date);
  const visible = loans.slice(0, MAX_VISIBLE_PER_DAY);
  const overflow = loans.length - visible.length;
  const dayTotal = loans.length ? formatCompactDollar(sumLoanAmounts(loans)) : null;

  return (
    <Box
      sx={{
        // minWidth:0 lets the 1fr grid track shrink below the cell's content
        // width, so every column stays exactly equal (chips truncate instead of
        // widening their column and knocking rows out of alignment).
        minWidth: 0,
        overflow: "hidden",
        minHeight: { xs: 92, sm: 128 },
        p: 1,
        borderRight: isLastCol ? "none" : `1px solid ${C.lineSoft}`,
        borderBottom: isLastRow ? "none" : `1px solid ${C.lineSoft}`,
        background: today ? C.todayBg : inMonth ? C.surface : "#fafbfc",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Flx ac jb sx={{ mb: 0.5 }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            fontSize: 13,
            fontWeight: 600,
            color: today ? "#fff" : inMonth ? C.sub : "#c4cad3",
            background: today ? C.accent : "transparent",
          }}
        >
          {format(date, "d")}
        </Box>
        {dayTotal && (
          <Txt sx={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 600, color: C.faint }}>
            {loans.length} · {dayTotal}
          </Txt>
        )}
      </Flx>

      <Box sx={{ minWidth: 0 }}>
        {visible.map((loan) => (
          <ClosingChip key={loanId(loan)} loan={loan} onClick={onSelectLoan} />
        ))}
        {overflow > 0 && (
          <Box
            onClick={(e) => setMoreAnchor(e.currentTarget)}
            sx={{
              px: 1,
              py: "3px",
              fontSize: 11.5,
              fontWeight: 600,
              color: C.accent,
              cursor: "pointer",
              borderRadius: "6px",
              width: "fit-content",
              "&:hover": { background: C.accentSoft },
            }}
          >
            +{overflow} more
          </Box>
        )}
      </Box>

      <Popover
        open={Boolean(moreAnchor)}
        anchorEl={moreAnchor}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 1, minWidth: 260, maxWidth: 320 } } }}
      >
        <Txt sx={{ fontSize: 12, fontWeight: 700, color: C.ink, mb: 0.75 }}>
          {format(date, "EEEE, MMM d")} · {loans.length} closing
          {loans.length === 1 ? "" : "s"}
        </Txt>
        {loans.map((loan) => (
          <ClosingChip
            key={loanId(loan)}
            loan={loan}
            onClick={(l) => {
              setMoreAnchor(null);
              onSelectLoan(l);
            }}
          />
        ))}
      </Popover>
    </Box>
  );
};

const CalendarMonthView = ({ monthDate, byDay, onSelectLoan }) => {
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(monthDate));
    const end = endOfWeek(endOfMonth(monthDate));
    return eachDayOfInterval({ start, end });
  }, [monthDate]);

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${C.line}`,
        background: C.surface,
        boxShadow: "0 1px 2px rgba(20,30,50,.06)",
      }}
    >
      {/* Weekday header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        {WEEKDAYS.map((w) => (
          <Box
            key={w}
            sx={{
              minWidth: 0,
              px: 1.5,
              py: 1.25,
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: C.faint,
            }}
          >
            {w}
          </Box>
        ))}
      </Box>

      {/* Day cells — one grid for the whole month so every column shares the
          same track sizing and rows stay aligned at any width. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {days.map((date, i) => (
          <DayCell
            key={dayKey(date)}
            date={date}
            inMonth={isSameMonth(date, monthDate)}
            loans={byDay.get(dayKey(date)) || []}
            onSelectLoan={onSelectLoan}
            isLastCol={i % 7 === 6}
            isLastRow={i >= days.length - 7}
          />
        ))}
      </Box>
    </Box>
  );
};

export default CalendarMonthView;
